import React, { useCallback, useState } from "react";
import { IconButton } from "@mui/material";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { CustomButtonVariant } from "../../../components/CustomButton/CustomButton";
import { IFinancialService } from "../../../modules/financial/models/IFinancialService";
import useDialogAlert from "../../../hooks/DialogAlert";
import { downloadOpenWindow } from "../../../utils/download";
import DetailsModal from "../../../components/ModalDetailsGeneric/ModalDetailsGeneric";
import { StatementRow } from "../models/StatementRows";

interface ActionButtonsTableProps {
  index: number;
  row: StatementRow;
  financialService: IFinancialService;
}

const ActionButtonsTable: React.FC<ActionButtonsTableProps> = ({
  row,
  financialService,
}) => {
  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { snackbar } = useDialogAlert();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const downloadNotes = useCallback(async () => {
    const typeService =
      row.category === "CreditNote"
        ? "CREDIT"
        : row.category === "DebitNote"
        ? "DEBIT"
        : undefined;

    if (!typeService) return;

    try {
      setIsDownloading(true);
      const response = await financialService.downloadCreditNoteOrDebitNote({
        service: typeService,
        customerNumber: row.customerNumber,
        documentNumber: row.invoiceId,
        companyCode: row.companyCode,
        fiscalYear: row.fiscalYear,
        documentItem: row.documentItem,
      });
      downloadOpenWindow(response, "pdf");
    } catch (error) {
      snackbar({
        message: "Erro ao fazer download da nota.",
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [row, financialService, snackbar]);

  const isDebitNote = row.category === "DebitNote";
  const isCreditNote = row.category === "CreditNote";

  const getDownloadAction = () => {
    if (isDebitNote) {
      return {
        label: "Baixar Nota de Débito",
        variant: isDownloading
          ? CustomButtonVariant.OUTLINED_LOADING
          : CustomButtonVariant.OUTLINED,
        onClick: downloadNotes,
      };
    }
    if (isCreditNote) {
      return {
        label: "Baixar Nota de Crédito",
        variant: isDownloading
          ? CustomButtonVariant.OUTLINED_LOADING
          : CustomButtonVariant.OUTLINED,
        onClick: downloadNotes,
      };
    }
    return null;
  };

  const downloadAction = getDownloadAction();

  const modalActions = [
    ...(downloadAction ? [downloadAction] : []),
    {
      label: "Fechar",
      variant: CustomButtonVariant.CONTAINED,
      onClick: handleClose,
    },
  ];

  return (
    <>
      <IconButton onClick={handleOpen}>
        <StyledIcon iconType="info" />
      </IconButton>

      <DetailsModal
        open={open}
        onClose={handleClose}
        title="Detalhes do Extrato"
        header={{
          title: row.customerName,
        }}
        data={[
          { label: "Documento", value: row.document },
          { label: "Categoria", value: row.categoryTranslation },
          { label: "Valor", value: row.value },
          { label: "Data de compensação", value: row.compensationDate || "-" },
          {
            label: "Forma de Pagamento",
            value: row.paymentMethod,
          },
          {
            label: "Chave de Acesso",
            value: row.accessKey || "-",
          },
          { label: "Banco", value: row.companyBank || "-" },
          { label: "Nosso Número", value: row.ourNumber || "-" },
          {
            label: "Descrição",
            value: row.description || "-",
            fullWidth: true,
          },
        ]}
        actions={modalActions}
      />
    </>
  );
};

export default ActionButtonsTable;
